"use client";

import { c as _c } from "react/compiler-runtime";
import Drop, { DropLocation } from "@/components/waves/drops/Drop";
import LightDrop from "@/components/waves/drops/LightDrop";
import VirtualScrollWrapper from "@/components/waves/drops/VirtualScrollWrapper";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { Drop as DropType, ExtendedDrop } from "@/helpers/waves/drop.helpers";
import { DropSize } from "@/helpers/waves/drop.helpers";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject } from "react";
import { memo, useCallback, useMemo } from "react";
import HighlightDropWrapper from "./HighlightDropWrapper";
import UnreadDivider from "./UnreadDivider";
type DropActionHandler = ({
  drop,
  partId
}: {
  drop: ApiDrop;
  partId: number;
}) => void;
interface DropsListProps {
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  readonly drops: DropType[];
  readonly showWaveInfo: boolean;
  readonly activeDrop: ActiveDropState | null;
  readonly showReplyAndQuote: boolean;
  readonly onReply: DropActionHandler;
  readonly onQuote: DropActionHandler;
  readonly onReplyClick: (serialNo: number) => void;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly serialNo: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null> | null;
  readonly dropViewDropId: string | null;
  readonly parentContainerRef?: React.RefObject<HTMLElement | null> | undefined;
  readonly location?: DropLocation | undefined;
  readonly unreadDividerSerialNo?: number | null | undefined;
}
const MemoizedDrop = memo(Drop);
const MemoizedLightDrop = memo(LightDrop);
const DropsList = memo(function DropsList(t0) {
  const $ = _c(27);
  const {
    scrollContainerRef,
    drops,
    showWaveInfo,
    activeDrop,
    showReplyAndQuote,
    onReply,
    onQuote,
    onReplyClick,
    serialNo,
    targetDropRef,
    parentContainerRef,
    onQuoteClick,
    onDropContentClick,
    dropViewDropId,
    location: t1,
    unreadDividerSerialNo
  } = t0;
  const location = t1 === undefined ? DropLocation.WAVE : t1;
  let t2;
  if ($[0] !== onReply) {
    t2 = t3 => {
      const {
        drop,
        partId
      } = t3;
      return onReply({
        drop,
        partId
      });
    };
    $[0] = onReply;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleReply = t2;
  let t3;
  if ($[2] !== onQuote) {
    t3 = t4 => {
      const {
        drop: drop_0,
        partId: partId_0
      } = t4;
      return onQuote({
        drop: drop_0,
        partId: partId_0
      });
    };
    $[2] = onQuote;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const handleQuote = t3;
  let t4;
  if ($[4] !== onReplyClick) {
    t4 = dropSerialNo => onReplyClick(dropSerialNo);
    $[4] = onReplyClick;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleReplyClick = t4;
  let t5;
  bb0: {
    if (location === DropLocation.WAVE) {
      let t6;
      if ($[6] !== drops) {
        t6 = [...drops].reverse();
        $[6] = drops;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      t5 = t6;
      break bb0;
    }
    t5 = drops;
  }
  const orderedDrops = t5;
  let t6;
  if ($[8] !== activeDrop || $[9] !== dropViewDropId || $[10] !== handleQuote || $[11] !== handleReply || $[12] !== handleReplyClick || $[13] !== onDropContentClick || $[14] !== onQuoteClick || $[15] !== parentContainerRef || $[16] !== scrollContainerRef || $[17] !== serialNo || $[18] !== showReplyAndQuote || $[19] !== showWaveInfo || $[20] !== targetDropRef) {
    t6 = {
      showWaveInfo,
      activeDrop,
      handleReply,
      handleQuote,
      showReplyAndQuote,
      serialNo,
      targetDropRef,
      handleReplyClick,
      onQuoteClick,
      parentContainerRef,
      dropViewDropId,
      onDropContentClick,
      scrollContainerRef
    };
    $[8] = activeDrop;
    $[9] = dropViewDropId;
    $[10] = handleQuote;
    $[11] = handleReply;
    $[12] = handleReplyClick;
    $[13] = onDropContentClick;
    $[14] = onQuoteClick;
    $[15] = parentContainerRef;
    $[16] = scrollContainerRef;
    $[17] = serialNo;
    $[18] = showReplyAndQuote;
    $[19] = showWaveInfo;
    $[20] = targetDropRef;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const getItemData = t6;
  let elements;
  if ($[22] !== getItemData || $[23] !== location || $[24] !== orderedDrops || $[25] !== unreadDividerSerialNo) {
    elements = [];
    for (let i = 0; i < orderedDrops.length; i++) {
      const drop_1 = orderedDrops[i];
      const previousDrop = orderedDrops[i - 1] ?? null;
      const nextDrop = orderedDrops[i + 1] ?? null;
      if (!drop_1) {
        continue;
      }
      if (unreadDividerSerialNo !== null && unreadDividerSerialNo !== undefined && drop_1.serial_no === unreadDividerSerialNo) {
        elements.push(<UnreadDivider key={`unread-divider-${unreadDividerSerialNo}`} />);
      }
      elements.push(<HighlightDropWrapper key={drop_1.stableKey} id={`drop-${drop_1.serial_no}`} waveDropId={drop_1.type === DropSize.FULL ? drop_1.stableHash ?? drop_1.id ?? drop_1.stableKey : undefined} ref={getItemData.serialNo === drop_1.serial_no ? getItemData.targetDropRef : null} active={getItemData.serialNo === drop_1.serial_no} scrollContainer={getItemData.scrollContainerRef?.current ?? null} className={getItemData.serialNo === drop_1.serial_no ? "tw-scroll-mt-20" : ""}><VirtualScrollWrapper scrollContainerRef={getItemData.scrollContainerRef} dropSerialNo={drop_1.serial_no} waveId={drop_1.type === DropSize.FULL ? drop_1.wave.id : drop_1.waveId} type={drop_1.type}>{drop_1.type === DropSize.FULL ? <MemoizedDrop dropViewDropId={getItemData.dropViewDropId} onReplyClick={getItemData.handleReplyClick} drop={drop_1} previousDrop={previousDrop} nextDrop={nextDrop} showWaveInfo={getItemData.showWaveInfo} activeDrop={getItemData.activeDrop} onReply={getItemData.handleReply} onQuote={getItemData.handleQuote} location={location} showReplyAndQuote={getItemData.showReplyAndQuote} onQuoteClick={getItemData.onQuoteClick} parentContainerRef={getItemData.parentContainerRef} onDropContentClick={getItemData.onDropContentClick} /> : <MemoizedLightDrop drop={drop_1} />}</VirtualScrollWrapper></HighlightDropWrapper>);
    }
    $[22] = getItemData;
    $[23] = location;
    $[24] = orderedDrops;
    $[25] = unreadDividerSerialNo;
    $[26] = elements;
  } else {
    elements = $[26];
  }
  const memoizedDrops = elements;
  return memoizedDrops;
});
export default DropsList;